// frontend/src/store/useWizardStore.js
import { create } from 'zustand';

export const useWizardStore = create((set, get) => ({

  editId: null,
  currentStep: 1,
  totalSteps: 4,
  migrationName: 'Nueva Migración',

  syncMode: 'append',
  frequencyType: 'once',
  cronExpression: '0 9 * * 1,2,3,4,5',
  timezone: 'America/Lima',

  sourceColumns: [],
  targetColumns: [],

  source: { connectionId: null, type: null, config: {} },
  target: { connectionId: null, type: null, config: {} },

  mapping: [],

  // 🆕 CAMPOS N:M
  multiTableMode: 0,           // 0: 1:1, 1: N:M
  tables: [],                  // Array de {sourceTableName, targetTableName, mapping, executionOrder, transformationType}
  selectedTableIdx: null,      // Tabla seleccionada para edición (null en 1:1)

  // 🆕 LISTAS SIMÉTRICAS (para flujo origen ↔ destino)
  sourceSelectedTables: [],    // Array de tabla origen seleccionadas
  targetSelectedTables: [],    // Array de tabla destino seleccionadas (debe tener igual length que source)

  sourceRelationships: [],    // Array de {tableFrom, columnFrom, rulesFrom[], tableTo, columnTo, rulesTo[], joinType}
  targetRelationships: [],    // Array de {tableFrom, columnFrom, rulesFrom[], tableTo, columnTo, rulesTo[], joinType}

  pipelineStatus: 1,
  notifications: { to: [], cc: [] },
  setNotifications: (notifs) => set({ notifications: notifs }),

  setPipelineStatus: (val) => set({
    pipelineStatus: val,
    isActive: val === 1
  }),

  isActive: true,
  setIsActive: (val) => set((state) => ({
    isActive: val,
    pipelineStatus: state.pipelineStatus === 0 ? 0 : (val ? 1 : 2)
  })),

  runMode: 'once',
  setRunMode: (val) => set({ runMode: val }),

  startDate: new Date().toISOString().split('T')[0],
  setStartDate: (val) => set({ startDate: val }),

  scheduleUI: null,
  setScheduleUI: (data) => set({ scheduleUI: data }),

  dedupConfig: { enabled: false, column: '' },
  setDedupConfig: (updater) => set((state) => ({
    dedupConfig: typeof updater === 'function' ? updater(state.dedupConfig) : updater
  })),

  filterConfig: {
    enabled: false, mode: 'auto', field: '',
    fromValue: null, toValue: null, watermarkValue: null,
  },
  setFilterConfig: (patch) => set((state) => ({
    filterConfig: { ...state.filterConfig, ...patch },
  })),

  hasEndDate: false,
  setHasEndDate: (val) => set({ hasEndDate: val }),
  endDate: '',
  setEndDate: (val) => set({ endDate: val }),

  // 🟢 FIX ARQUITECTURA: Carga limpia sin disparar los "setters" destructivos
  loadMigrationData: (apiData) => set((state) => {
    const params = apiData.parameters || {};

    // 🆕 Detectar modo correctamente: 1:1, 1:N, N:1, N:M
    const sourceTableList = params.source?.selectedTables || apiData.multiTableMappings?.tables?.map(t => t.sourceTableName) || [];
    const targetTableList = params.target?.selectedTables || apiData.multiTableMappings?.tables?.map(t => t.targetTableName) || [];

    const srcCount = sourceTableList.length || (params.source?.selectedTable ? 1 : 0);
    const tgtCount = targetTableList.length || (params.target?.selectedTable ? 1 : 0);

    let mode = '1:1';  // Default
    if (srcCount === 1 && tgtCount > 1) mode = '1:N';
    else if (srcCount > 1 && tgtCount === 1) mode = 'N:1';
    else if (srcCount > 1 && tgtCount > 1) mode = 'N:M';

    return {
      editId: apiData.idConfigHead || apiData.id,
      migrationName: apiData.name,
      runMode: params.runMode || (apiData.frequencyType === 'scheduled' ? 'scheduled' : 'once'),
      syncMode: apiData.syncMode || 'append',
      frequencyType: apiData.frequencyType || 'once',
      cronExpression: apiData.cronExpression || '0 9 * * 1,2,3,4,5',
      timezone: apiData.timezone || 'America/Lima',
      scheduleUI: params.scheduleUI || null,

      pipelineStatus: apiData.status,
      isActive: apiData.status === 1,

      startDate: params.startDate || new Date().toISOString().split('T')[0],
      hasEndDate: params.hasEndDate || false,
      endDate: params.endDate || '',
      dedupConfig: {
        enabled: params.avoidDuplicates === 1,
        column: params.dedupColumn || ''
      },
      notifications: params.notifications || { to: [], cc: [] },
      filterConfig: params.filterConfig
        ? {
          enabled: false, mode: 'auto', field: '', fromValue: null,
          toValue: null, watermarkValue: null, ...params.filterConfig
        }
        : {
          enabled: false, mode: 'auto', field: '', fromValue: null,
          toValue: null, watermarkValue: null
        },

      // source: params.source || { connectionId: null, type: null, config: {} },
      // target: params.target || { connectionId: null, type: null, config: {} },

      source: (() => {
        const src = params.source || { connectionId: null, type: null, config: {} };
        // 🆕 Asegurar selectedTable SOLO en 1:1
        if (src.config && srcCount === 1 && tgtCount === 1) {
          src.config.selectedTable = params.source?.config?.selectedTable || sourceTableList[0] || '';
        }
        return src;
      })(),

      target: (() => {
        const tgt = params.target || { connectionId: null, type: null, config: {} };
        // 🆕 Asegurar selectedTable SOLO en 1:1
        if (tgt.config && srcCount === 1 && tgtCount === 1) {
          tgt.config.selectedTable = params.target?.config?.selectedTable || targetTableList[0] || '';
        }
        return tgt;
      })(),


      // 🆕 Cargar listas según modo
      sourceSelectedTables: sourceTableList,  // Array vacío si es 1:1
      targetSelectedTables: targetTableList,  // Array vacío si es 1:1

      multiTableMode: (mode === 'N:M' || mode === '1:N' || mode === 'N:1') ? 1 : 0,
      tables: apiData.multiTableMappings?.tables || [],

      mapping: !apiData.multiTableMappings ? (apiData.mapping || []) : [],

      // 🔧 Normalizar: pipelines guardados antes de v2 no tienen rulesFrom/rulesTo
      // 🔧 Las relationships están nested en params.source/target, no al nivel de params
      sourceRelationships: (params.source?.relationships || []).map(r => ({
        ...r, rulesFrom: r.rulesFrom || [], rulesTo: r.rulesTo || [],
      })),
      targetRelationships: (params.target?.relationships || []).map(r => ({
        tableFrom: r.tableFrom || '',
        tableTo: r.tableTo || '',
        columnFrom: r.columnFrom || '',
        columnTo: r.columnTo || '',
        joinType: r.joinType || 'FK',
        rulesFrom: r.rulesFrom || [],
        rulesTo: r.rulesTo || [],
        anchorSource: r.anchorSource || '',   // ← nuevo
        bridgeColumn: r.bridgeColumn || '',   // ← nuevo
      })),

      // R.2 Fix: restaurar columnas para FilterConfigPanel en edit mode
      sourceColumns: params.source?.columns || [],
      targetColumns: params.target?.columns || [],

      currentStep: 1
    };
  }),

  // nextStep: () => set((state) => ({ currentStep: Math.min(state.currentStep + 1, state.totalSteps) })),
  nextStep: () => set((state) => {
    // 🔧 FIX: totalSteps es estático (4) pero el wizard puede tener 4, 5 o 6 pasos
    // según el modo (1:1 → 4, N:1/1:N → 5, N:M → 6). Calcularlo dinámicamente
    // desde el estado real evita que nextStep() quede clampeado en modos multi-tabla.
    const srcCount = state.sourceSelectedTables.length > 0
      ? state.sourceSelectedTables.length
      : (state.source?.config?.selectedTable ? 1 : 0);
    const tgtCount = state.targetSelectedTables.length > 0
      ? state.targetSelectedTables.length
      : (state.target?.config?.selectedTable ? 1 : 0);

    const maxSteps = (srcCount > 1 && tgtCount > 1) ? 6   // N:M
      : (srcCount > 1 || tgtCount > 1) ? 5               // N:1 o 1:N
        : 4;                                                // 1:1

    return { currentStep: Math.min(state.currentStep + 1, maxSteps) };
  }),
  prevStep: () => set((state) => ({ currentStep: Math.max(state.currentStep - 1, 1) })),
  goToStep: (step) => set({ currentStep: step }),

  setMigrationName: (name) => set({ migrationName: name }),
  setSyncMode: (val) => set({ syncMode: val }),
  setFrequencyType: (type) => set({ frequencyType: type }),
  setCronExpression: (cron) => set({ cronExpression: cron }),
  setTimezone: (val) => set({ timezone: val }),

  setSourceColumns: (columns) => set({ sourceColumns: columns }),
  setTargetColumns: (columns) => set({ targetColumns: columns }),

  // 🆕 SETTERS PARA RELACIONES
  setSourceRelationships: (relationships) => set({ sourceRelationships: relationships }),
  setTargetRelationships: (relationships) => set({ targetRelationships: relationships }),

  setSource: (payload) => set((state) => {
    console.log('📝 setSource CALLED:', { payload, antes: state.source.config?.selectedTable });

    // 🟢 Solo borramos si NO estamos en modo edición inicial y el usuario cambia explícitamente la tabla
    const isTableChange =
      state.source.config?.selectedTable &&
      payload.config?.selectedTable &&
      state.source.config.selectedTable !== payload.config.selectedTable;

    if (isTableChange && !state.editId) {
      console.warn("⚠️ Cambio de tabla origen detectado. Limpiando mapeo...");
      console.log('   BEFORE:', state.source.config?.selectedTable);
      console.log('   AFTER:', payload.config?.selectedTable);
      return { source: { ...state.source, ...payload }, sourceColumns: [], mapping: [] };
    }
    const result = { source: { ...state.source, ...payload } };
    console.log('✅ setSource resultado:', result.source.config?.selectedTable);
    return result;
    // return { source: { ...state.source, ...payload } };
  }),

  setTarget: (payload) => set((state) => {
    console.log('📝 setTarget CALLED:', { payload });
    console.log('   source ANTES:', state.source.config?.selectedTable);

    // 🔧 FIX: Deep-merge de config para no pisar selectedTable/availableTables
    // Si el payload incluye config, se mezcla con el config existente en lugar de reemplazarlo.
    // Cualquier campo que quiera borrarse debe pasarse explícitamente como '' o null.
    const mergedConfig = payload.config !== undefined
      ? { ...(state.target.config || {}), ...payload.config }
      : state.target.config;

    const mergedPayload = { ...payload, config: mergedConfig };

    const isTableChange =
      state.target.config?.selectedTable &&
      mergedConfig?.selectedTable &&
      state.target.config.selectedTable !== mergedConfig.selectedTable;

    if (isTableChange && !state.editId) {
      console.warn("⚠️ Cambio de tabla destino detectado. Limpiando mapeo...");
      return { target: { ...state.target, ...mergedPayload }, targetColumns: [], mapping: [] };
    }
    return { target: { ...state.target, ...mergedPayload } };
  }),

  setMapping: (mappingArray) => set({ mapping: mappingArray }),

  // 🆕 MÉTODOS N:M
  setMultiTableMode: (mode) => set({ multiTableMode: mode }),

  addTable: (table) => set((state) => {
    const newTable = {
      sourceTableName: table.sourceTableName || '',
      targetTableName: table.targetTableName || '',
      mapping: table.mapping || [],
      executionOrder: state.tables.length + 1,
      transformationType: table.transformationType || 'DIRECT',
      joinCondition: table.joinCondition || null,
      splitRules: table.splitRules || null,
    };
    return {
      tables: [...state.tables, newTable]
    };
  }),

  updateTable: (idx, updates) => set((state) => {
    const newTables = [...state.tables];
    newTables[idx] = { ...newTables[idx], ...updates };
    return { tables: newTables };
  }),

  removeTable: (idx) => set((state) => {
    const newTables = state.tables.filter((_, i) => i !== idx);
    // Recalcular executionOrder
    const reordered = newTables.map((table, i) => ({
      ...table,
      executionOrder: i + 1
    }));
    const newSelectedIdx = state.selectedTableIdx === idx ? null : state.selectedTableIdx;
    return {
      tables: reordered,
      selectedTableIdx: newSelectedIdx
    };
  }),

  setSelectedTableIdx: (idx) => set({ selectedTableIdx: idx }),

  reorderTables: (fromIdx, toIdx) => set((state) => {
    const newTables = [...state.tables];
    const [movedTable] = newTables.splice(fromIdx, 1);
    newTables.splice(toIdx, 0, movedTable);

    // Recalcular executionOrder
    const reordered = newTables.map((table, i) => ({
      ...table,
      executionOrder: i + 1
    }));
    return { tables: reordered };
  }),

  // 🆕 MÉTODOS PARA LISTAS SIMÉTRICAS
  addSourceTable: (tableName) => set((state) => ({
    sourceSelectedTables: [...state.sourceSelectedTables, tableName],
    multiTableMode: 1  // Activar modo N:M al agregar segunda tabla
  })),

  removeSourceTable: (idx) => set((state) => {
    const removedTable = state.sourceSelectedTables[idx];
    const newSourceTables = state.sourceSelectedTables.filter((_, i) => i !== idx);
    const newMode = newSourceTables.length === 0 ? 0 : 1;

    // 🔧 Limpiar relaciones que involucran la tabla eliminada
    const newRelationships = (state.sourceRelationships || []).filter(
      r => r.tableFrom !== removedTable && r.tableTo !== removedTable
    );

    return {
      sourceSelectedTables: newSourceTables,
      sourceRelationships: newRelationships,
      multiTableMode: newMode
    };
  }),

  reorderSourceTables: (fromIdx, toIdx) => set((state) => {
    const newSourceTables = [...state.sourceSelectedTables];
    const [moved] = newSourceTables.splice(fromIdx, 1);
    newSourceTables.splice(toIdx, 0, moved);
    return { sourceSelectedTables: newSourceTables };
  }),

  addTargetTable: (tableName) => set((state) => ({
    targetSelectedTables: [...state.targetSelectedTables, tableName],
    multiTableMode: 1
  })),

  removeTargetTable: (idx) => set((state) => {
    const removedTable = state.targetSelectedTables[idx];
    const newTargetTables = state.targetSelectedTables.filter((_, i) => i !== idx);
    const newMode = newTargetTables.length === 0 ? 0 : 1;

    // 🔧 Limpiar relaciones que involucran la tabla eliminada
    const newRelationships = (state.targetRelationships || []).filter(
      r => r.tableFrom !== removedTable && r.tableTo !== removedTable
    );

    return {
      targetSelectedTables: newTargetTables,
      targetRelationships: newRelationships,
      multiTableMode: newMode
    };
  }),

  reorderTargetTables: (fromIdx, toIdx) => set((state) => {
    const newTargetTables = [...state.targetSelectedTables];
    const [moved] = newTargetTables.splice(fromIdx, 1);
    newTargetTables.splice(toIdx, 0, moved);
    return { targetSelectedTables: newTargetTables };
  }),

  // 🆕 VALIDACIÓN Y DETECCIÓN
  validateTableCount: () => {
    const state = get();
    return state.sourceSelectedTables.length === state.targetSelectedTables.length;
  },

  // isMultiTable: () => {
  //   const state = get();
  //   return state.sourceSelectedTables.length > 1 || state.targetSelectedTables.length > 1;
  // },

  // 🆕 DETECTAR MODO: 1:1, 1:N, N:1, o N:M
  getWizardMode: () => {
    const state = get();

    // 🆕 Contar tablas: usar sourceSelectedTables/targetSelectedTables si existen, sino config.selectedTable
    const srcCount = state.sourceSelectedTables.length > 0
      ? state.sourceSelectedTables.length
      : (state.source?.config?.selectedTable ? 1 : 0);

    const tgtCount = state.targetSelectedTables.length > 0
      ? state.targetSelectedTables.length
      : (state.target?.config?.selectedTable ? 1 : 0);

    // 🆕 N:1: Si origen tiene 2+, aunque target aún esté vacío (estamos EN StepSource)
    if (srcCount > 1) {
      if (tgtCount > 1) return 'N:M';     // N:M: ambos lados tienen múltiples
      if (tgtCount === 1) return 'N:1';   // N:1: origen múltiple, destino 1
      // Si tgtCount === 0 (aún en StepSource), también es N:1
      return 'N:1';
    }

    // 🆕 1:N: Si destino tiene 2+
    if (tgtCount > 1) return '1:N';

    // 1:1: Por defecto
    return '1:1';
  },

  isMultiTable: () => {
    const state = get();
    return state.sourceSelectedTables.length > 1 || state.targetSelectedTables.length > 1;
  },

  getPayload: () => {
    const state = get();
    const cleanConfig = (configObj) => {
      if (!configObj) return {};
      // const { tables, collections, availableTables, ...cleanData } = configObj;
      const { tables, collections, ...cleanData } = configObj;

      return cleanData;
    };

    // 🔧 N:M: incluir selectedTables + relationships en source/target de parameters.
    // _run_etl_n_to_m los lee de params["source"]["selectedTables"] etc.
    // En 1:1 estas claves quedan undefined → JSON.parse los omite → sin riesgo.
    const srcTables = state.sourceSelectedTables || [];
    const tgtTables = state.targetSelectedTables || [];
    const srcRels = state.sourceRelationships || [];
    const tgtRels = state.targetRelationships || [];

    const basePayload = {
      idConfigHead: state.editId,
      status: state.pipelineStatus,
      runMode: state.runMode,
      name: state.migrationName,
      syncMode: state.syncMode,
      frequencyType: state.isActive ? state.runMode : 'draft',
      cronExpression: state.cronExpression || null,
      timezone: state.timezone,
      parameters: {
        idConfigHead: state.editId,
        runMode: state.runMode,
        startDate: state.startDate,
        hasEndDate: state.hasEndDate,
        endDate: state.hasEndDate && state.endDate !== '' ? state.endDate : null,
        avoidDuplicates: state.dedupConfig.enabled ? 1 : 0,
        dedupColumn: state.dedupConfig.enabled ? state.dedupConfig.column : null,
        scheduleUI: state.scheduleUI,
        notifications: state.notifications,
        filterConfig: state.filterConfig,

        // 🔧 source: incluir selectedTables y relationships si existen (N:M)
        source: {
          type: state.source.type,
          config: cleanConfig(state.source.config),
          ...(srcTables.length > 0 && { selectedTables: srcTables }),
          ...(srcRels.length > 0 && { relationships: srcRels }),
        },
        // 🔧 target: incluir selectedTables y relationships si existen (1:N / N:M)
        target: {
          type: state.target.type,
          config: cleanConfig(state.target.config),
          ...(tgtTables.length > 0 && { selectedTables: tgtTables }),
          ...(tgtRels.length > 0 && { relationships: tgtRels }),
        },
      }
    };

    // N:M / N:1: sourceSelectedTables tiene > 0 tablas
    if (srcTables.length > 0) {
      const isMultiTable = srcTables.length > 1 || tgtTables.length > 1;
      return {
        ...basePayload,
        mapping: state.mapping,
        ...(isMultiTable && { multiTableMode: 1 }),
      };
    }

    // 1:N: target tiene múltiples tablas, source tiene 1 (en config.selectedTable)
    // basePayload.parameters.target.selectedTables ya está incluido arriba
    // El dispatcher tasks.py lo detecta vía parameters.target.selectedTables
    if (tgtTables.length > 1) {
      return {
        ...basePayload,
        mapping: state.mapping,
        multiTableMode: 1,
      };
    }

    // 1:1: flujo estándar
    return {
      ...basePayload,
      mapping: state.mapping,
    };
  },

  resetWizard: () => set({
    editId: null,
    currentStep: 1,
    pipelineStatus: 1,
    migrationName: 'Nueva Migración',
    syncMode: 'append',
    frequencyType: 'once',
    cronExpression: '0 9 * * 1,2,3,4,5',
    timezone: 'America/Lima',
    scheduleUI: null,
    isActive: true,
    runMode: 'once',
    startDate: new Date().toISOString().split('T')[0],
    sourceColumns: [],
    targetColumns: [],
    dedupConfig: { enabled: false, column: '' },  // 🔧 reset al crear nuevo pipeline
    filterConfig: {
      enabled: false, mode: 'auto', field: '',
      fromValue: null, toValue: null, watermarkValue: null,
    },
    source: { connectionId: null, type: null, config: {} },
    target: { connectionId: null, type: null, config: {} },
    mapping: [],

    // 🆕 Reset N:M
    multiTableMode: 0,
    tables: [],
    selectedTableIdx: null,
    sourceSelectedTables: [],  // 🆕 Reset listas simétricas
    targetSelectedTables: [],  // 🆕

    notifications: { to: [], cc: [] },

    sourceRelationships: [],
    targetRelationships: [],
  }),
  // 🆕 NUEVO: Limpiar tablas múltiples sin resetear el wizard
  clearSelectedTables: (mode) => set((state) => ({
    sourceSelectedTables: mode === 'source' ? [] : state.sourceSelectedTables,
    targetSelectedTables: mode === 'target' ? [] : state.targetSelectedTables,
    multiTableMode: 0
  })),
}));


